import type { Drill } from '@/lib/drill-taxonomy/drill-types';

const toArray = (value: unknown): string[] => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return [value.trim()].filter(Boolean);
  }

  return [];
};

/**
 * Retrieve tag values for a given taxonomy category, handling legacy structures.
 */
export const getTagValues = (drill: Drill, category: string): string[] => {
  if (!drill) return [];

  let values: string[] = [];

  const directMatches = toArray(drill.tags?.[category]);
  if (directMatches.length > 0) {
    values = directMatches;
  } else {
    // Legacy lowercase keys (e.g., mainCategory, domain)
    const legacyKey = category.toLowerCase();
    const legacyMatches = toArray((drill.tags as any)?.[legacyKey]);
    if (legacyMatches.length > 0) {
      values = legacyMatches;
    } else if (category === 'Domain') {
      // Legacy nested domain structure
      const legacyDomain = toArray((drill.tags as any)?.mainCategory);
      if (legacyDomain.length > 0) {
        values = legacyDomain;
      }
    }
  }

  // Dynamic reclassification normalization for backward compatibility
  if (category === 'Movement Limitation') {
    return values.filter((v) => v !== 'Improve Pelvic & Femoral IR');
  }

  if (category === 'Biomechanical Intent') {
    // Check if legacy "Movement Limitation" has "Improve Pelvic & Femoral IR"
    const hasLegacyIR = toArray(drill.tags?.['Movement Limitation']).includes('Improve Pelvic & Femoral IR') ||
                        toArray((drill.tags as any)?.['movement limitation']).includes('Improve Pelvic & Femoral IR') ||
                        toArray((drill.tags as any)?.['movementLimitation']).includes('Improve Pelvic & Femoral IR');
    if (hasLegacyIR && !values.includes('Improve Pelvic & Femoral IR')) {
      return [...values, 'Improve Pelvic & Femoral IR'];
    }
  }

  return values;
};

/**
 * Retrieve the primary (first) tag value for a taxonomy category.
 */
export const getPrimaryTagValue = (drill: Drill, category: string): string | undefined => {
  const values = getTagValues(drill, category);
  return values.length > 0 ? values[0] : undefined;
};

/**
 * Retrieve the drill's Domain (primary classification).
 */
export const getDrillDomain = (drill: Drill): string => {
  const domainFromTags = getPrimaryTagValue(drill, 'Domain');
  if (domainFromTags) {
    return domainFromTags;
  }

  if (typeof drill.mainCategory === 'string' && drill.mainCategory.trim().length > 0) {
    return drill.mainCategory.trim();
  }

  return 'Unknown';
};

/** Domains the library cycle button rotates through. */
export const CYCLABLE_DOMAINS = ['Weightroom', 'Positional', 'Rolling'] as const;

export type CycledDomain = (typeof CYCLABLE_DOMAINS)[number];

const CYCLABLE_DOMAIN_SET: ReadonlySet<string> = new Set(CYCLABLE_DOMAINS);

/**
 * Return the selected cyclable Domain when exactly one of Weightroom / Positional / Rolling is selected.
 */
export function currentCycledDomain(selected: Iterable<string> | undefined): CycledDomain | undefined {
  const values = Array.from(selected ?? []);
  if (values.length !== 1) {
    return undefined;
  }
  const [value] = values;
  return CYCLABLE_DOMAIN_SET.has(value) ? (value as CycledDomain) : undefined;
}

/**
 * Next Domain in Weightroom → Positional → Rolling. Unknown / empty selection starts at Weightroom.
 */
export function nextCycledDomain(current: string | undefined): CycledDomain {
  const idx = CYCLABLE_DOMAINS.findIndex((domain) => domain === current);
  if (idx === -1) {
    return CYCLABLE_DOMAINS[0];
  }
  return CYCLABLE_DOMAINS[(idx + 1) % CYCLABLE_DOMAINS.length];
}


